import { useEffect, useState } from "react";

/**
 * Live loudness of a microphone stream, as one level per waveform bar.
 *
 * Each bar is its own slice of the voice band, read per animation frame off
 * an `AnalyserNode`'s frequency data — a real equaliser, so the bars move
 * against each other rather than in step. Levels are 0..1. Answers null with
 * no stream or where the browser offers no `AudioContext`; the strip then
 * falls back to an idle animation rather than a dead one.
 */
export function useMicLevels(
  stream: MediaStream | null,
  bars: number,
): number[] | null {
  const [levels, setLevels] = useState<number[] | null>(null);

  useEffect(() => {
    const Context =
      (globalThis.AudioContext as typeof AudioContext | undefined) ??
      (globalThis as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    // Nothing to listen to: the previous effect's cleanup has already put the
    // levels back to null, so there is nothing to set here.
    if (stream === null || Context === undefined) return;

    const context = new Context();
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.75;
    context.createMediaStreamSource(stream).connect(analyser);
    const samples = new Uint8Array(analyser.frequencyBinCount);
    // The voice lives in the lower bins; the top of the spectrum would pin
    // half the bars to the floor.
    const voiced = Math.max(bars, Math.floor(samples.length / 3));
    const band = voiced / bars;
    let frame = 0;

    const read = () => {
      analyser.getByteFrequencyData(samples);
      const next = Array.from({ length: bars }, (_, bar) => {
        const from = Math.floor(bar * band);
        const to = Math.max(from + 1, Math.floor((bar + 1) * band));
        let sum = 0;
        for (let bin = from; bin < to; bin += 1) sum += samples[bin] ?? 0;
        return Math.min(1, sum / (to - from) / 255 / 0.6);
      });
      setLevels(next);
      frame = requestAnimationFrame(read);
    };
    frame = requestAnimationFrame(read);

    return () => {
      cancelAnimationFrame(frame);
      void context.close();
      setLevels(null);
    };
  }, [stream, bars]);

  return levels;
}
